import { Button, DatePicker, Input, Modal, Rate } from 'antd'
import { observer } from 'mobx-react-lite'
import { Fragment, useEffect, useState } from 'react'
import { MdAdd, MdClose } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'
import { useNotification } from '@/Ship/Hooks/useNotification'

import { CompleteTaskModalStore } from '../../Stores/CompleteTaskModalStore'

import styles from './CompleteTaskModal.module.css'

interface CompleteTaskModalProps {
    assignmentUuid: string
    assignment: any
    open: boolean
    onClose: () => void
    onCompleted: () => void
}

/**
 * Завершення завдання диспетчером: оцінка водія за кожен день роботи (рядки від планової
 * дати навантаження до сьогодні готує стор) і перехід у «Завершено водієм». Зверху — кого
 * оцінюємо і що станеться після підтвердження; дні — таблицею, день у рядок.
 */
export const CompleteTaskModal = observer(
    ({ assignmentUuid, assignment, open, onClose, onCompleted }: CompleteTaskModalProps) => {
        const [store] = useState(() => new CompleteTaskModalStore())
        const { showSuccess, showError } = useNotification()

        useEffect(() => {
            if (open) store.initFromAssignment(assignment)
        }, [open, assignment?.plannedLoadingDate])

        const handleSubmit = async () => {
            const ok = await store.submit(assignmentUuid, { success: showSuccess, error: showError })
            if (ok) {
                onCompleted()
                onClose()
            }
        }

        const driverName: string | null = assignment?.driver?.fullName || assignment?.driver?.shortName || null
        const canRemove = store.rows.length > 1

        return (
            <Modal
                open={open}
                title="Завершення завдання"
                onCancel={onClose}
                width={640}
                footer={[
                    <Button key="cancel" onClick={onClose} disabled={store.loading}>
                        Скасувати
                    </Button>,
                    <Button key="submit" type="primary" loading={store.loading} onClick={handleSubmit}>
                        Завершити завдання
                    </Button>,
                ]}
            >
                <div className={cx(styles, 'intro')}>
                    <div className={cx(styles, 'who')}>
                        {driverName ? (
                            <>
                                Водій: <b>{driverName}</b>
                            </>
                        ) : (
                            'Водій не призначений'
                        )}
                        {assignment?.id ? ` · Завдання #${assignment.id}` : ''}
                    </div>
                    <div className={cx(styles, 'hint')}>
                        Оцініть роботу водія за кожен день. Після підтвердження завдання перейде у статус «Завершено
                        водієм».
                    </div>
                </div>

                <div className={cx(styles, 'table')} role="table" aria-label="Оцінки за дні">
                    <div className={cx(styles, 'head')} role="columnheader">
                        Дата
                    </div>
                    <div className={cx(styles, 'head')} role="columnheader">
                        Оцінка
                    </div>
                    <div className={cx(styles, 'head')} role="columnheader">
                        Коментар
                    </div>
                    <div className={cx(styles, 'headSpacer')} aria-hidden="true" />

                    {store.rows.map((row, i) => (
                        <Fragment key={i}>
                            <DatePicker
                                value={row.date}
                                format="DD.MM.YYYY"
                                allowClear={false}
                                aria-label={`Дата, день ${i + 1}`}
                                onChange={(d) => d && store.updateRow(i, { date: d.startOf('day') })}
                            />
                            <Rate
                                className={cx(styles, 'rate')}
                                value={row.rating}
                                count={5}
                                aria-label={`Оцінка, день ${i + 1}`}
                                onChange={(v) => store.updateRow(i, { rating: v })}
                            />
                            <Input.TextArea
                                className={cx(styles, 'comment')}
                                autoSize={{ minRows: 1, maxRows: 4 }}
                                placeholder="Необов'язково"
                                aria-label={`Коментар, день ${i + 1}`}
                                value={row.comment}
                                onChange={(e) => store.updateRow(i, { comment: e.target.value })}
                            />
                            {canRemove ? (
                                <Button
                                    type="text"
                                    size="small"
                                    className={cx(styles, 'remove')}
                                    icon={<MdClose />}
                                    aria-label={`Прибрати день ${i + 1}`}
                                    title="Прибрати день"
                                    onClick={() => store.removeRow(i)}
                                />
                            ) : (
                                <span aria-hidden="true" />
                            )}
                        </Fragment>
                    ))}
                </div>

                <Button type="link" className={cx(styles, 'add')} icon={<MdAdd />} onClick={() => store.addRow()}>
                    Додати день
                </Button>
            </Modal>
        )
    }
)
